import * as React from "react";
import { Button, Form, Input, Select } from "antd";
import { onError } from "src/common/onError";
import {
  ZTooltipExtFields,
  zTooltipExtFields,
} from "src/components/ExtendedTooltip";
import { TooltipPlacement } from "antd/lib/tooltip";
import { ExtendedTooltip } from "src/components/ExtendedTooltip/ExtendedTooltip";
import { ModalVertFixed } from "src/components/ModalVertFixed/ModalVertFixed";
import { DeleteFilled } from "@ant-design/icons";
import { MultiLangFields } from "src/components/MultiLangFields";
import { clearObjectFields } from "src/common/clearObjectFields";
import styles from "./TooltipExtEdit.module.less";

interface PropsTooltipExtEdit {
  open: boolean;
  onCancel(): void;
  getInitialData(): ZTooltipExtFields;
  onOk(fields: ZTooltipExtFields): void;
  tooltipText: string | undefined;
}

// Чтобы избежать ошибок в строковых названиях
const field = (name: keyof ZTooltipExtFields) => name;

export const TooltipExtEdit: React.FC<PropsTooltipExtEdit> = (props) => {
  const { open, onCancel, onOk, getInitialData, tooltipText } = props;
  React.useEffect(() => {
    form.setFieldsValue(getInitialData());
  }, []);
  const [form] = Form.useForm();
  React.useEffect(() => {
    form.setFieldValue("text", tooltipText);
  }, [tooltipText]);
  const [previewData, setPreviewData] = React.useState<ZTooltipExtFields>({});
  React.useEffect(() => {
    if (open) setPreviewData({ ...getInitialData(), text: tooltipText });
  }, [open]);
  type PlacementOption = {
    value: TooltipPlacement;
    label: string;
  };
  const placementOptions: PlacementOption[] = React.useMemo(() => {
    const dict: Record<TooltipPlacement, string> = {
      topLeft: "Сверху слева",
      top: "Сверху",
      topRight: "Сверху справа",
      rightTop: "Справа сверху",
      right: "Справа",
      rightBottom: "Справа снизу",
      bottomRight: "Снизу справа",
      bottom: "Снизу",
      bottomLeft: "Снизу слева",
      leftBottom: "Слева снизу",
      left: "Слева",
      leftTop: "Слева сверху",
    };
    return Object.entries(dict).map(([value, label]) => ({
      value: value as TooltipPlacement,
      label,
    }));
  }, []);
  const onReset = () => {
    const newFields = clearObjectFields(form.getFieldsValue());
    form.setFieldsValue(newFields);
  };

  return (
    <ModalVertFixed
      height="90vh"
      title="Расширенные параметры для всплывающей подсказки"
      open={open}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      okButtonProps={{ htmlType: "submit" }}
      okText="Применить"
      centered
      width="600px"
      /* eslint react/no-unstable-nested-components: "off" */
      modalRender={(content) => (
        <Form
          form={form}
          initialValues={getInitialData()}
          className={styles.form}
          layout="vertical"
          onFinish={(values) => {
            try {
              onOk(zTooltipExtFields.parse(values));
            } catch (e) {
              onError(e);
            }
          }}
          onValuesChange={(part, all) => {
            setPreviewData(all);
          }}
        >
          {content}
        </Form>
      )}
    >
      <div className={styles.content}>
        <div className={styles.preview}>
          <ExtendedTooltip fields={previewData}>
            <Button>Просмотр</Button>
          </ExtendedTooltip>
          <Button
            type="text"
            icon={<DeleteFilled />}
            onClick={() => onReset()}
          />
        </div>
        <div className={styles.fields}>
          <Form.Item name={field("placement")} label="Положение">
            <Select options={placementOptions} allowClear />
          </Form.Item>
          <Form.Item
            name={field("title")}
            label="Заголовок"
            rules={[{ max: 50, message: "Не более 50 символов" }]}
          >
            <Input allowClear />
          </Form.Item>
          <MultiLangFields basePath={[field("titleLoc")]} />
          <Form.Item name={field("text")} label="Текст">
            <Input.TextArea allowClear />
          </Form.Item>
          <MultiLangFields basePath={[field("textLoc")]} />
          <Form.Item name={field("image")} label="Ссылка на изображение">
            <Input allowClear />
          </Form.Item>
          <Form.Item
            name={field("link")}
            label="Адрес ссылки"
            dependencies={[field("linkText")]}
            rules={[
              ({ getFieldValue }) => ({
                validator: async (_rule, value) => {
                  if (!value && getFieldValue(field("linkText")))
                    throw Error(
                      "Если есть текст ссылки, то надо заполнить адрес",
                    );
                },
              }),
            ]}
          >
            <Input allowClear />
          </Form.Item>
          <Form.Item name={field("linkText")} label="Текст ссылки">
            <Input allowClear />
          </Form.Item>
          <MultiLangFields basePath={[field("linkTextLoc")]} />
          <Form.Item
            name={field("download")}
            label="Адрес для скачивания"
            tooltip="Чтобы скачивание правильно работало, нужно чтобы в ответе сервера присутствовал заголовок Content-Disposition"
            dependencies={[field("downloadText")]}
            rules={[
              ({ getFieldValue }) => ({
                validator: async (_rule, value) => {
                  if (!value && getFieldValue(field("downloadText")))
                    throw Error(
                      "Если есть имя для скачивания, то должнен быть адрес для скачивания",
                    );
                },
              }),
            ]}
          >
            <Input allowClear />
          </Form.Item>
          <Form.Item
            name={field("downloadText")}
            label="Под каким именем скачать файл"
          >
            <Input allowClear />
          </Form.Item>
        </div>
      </div>
    </ModalVertFixed>
  );
};
